import { Timestamp } from "firebase/firestore";
import {
  CreatePollData,
  Poll,
  PollOption,
  PollQuestion,
  PollVoterVisibility,
  RawPoll,
  RawQuestion,
} from "../types/poll.types";

export const MAX_QUESTIONS = 10;
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 10;
export const MAX_QUESTION_LENGTH = 300;
export const MAX_OPTION_LENGTH = 120;

/** The question a poll written before this existed is read as. */
export const LEGACY_QUESTION_ID = "q1";

/**
 * Polls created before the setting existed are anonymous: nobody was told their
 * name could be shown.
 */
export const DEFAULT_VOTER_VISIBILITY: PollVoterVisibility = "anonymous";

/** Dates arrive as a Timestamp, as its plain form, or as a string. */
export function toDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof (value as Timestamp).toDate === "function") {
    return (value as Timestamp).toDate();
  }

  const seconds = (value as { seconds?: number; _seconds?: number })?.seconds
    ?? (value as { _seconds?: number })?._seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);

  return new Date(String(value));
}

function normalizeOption(raw: Partial<PollOption> | undefined, index: number): PollOption {
  const votes = Number(raw?.votes);

  return {
    id: String(raw?.id ?? `o${index + 1}`),
    text: String(raw?.text ?? ""),
    votes: Number.isFinite(votes) && votes > 0 ? votes : 0,
    order: Number.isFinite(Number(raw?.order)) ? Number(raw?.order) : index,
  };
}

function normalizeQuestion(raw: RawQuestion | undefined, index: number): PollQuestion {
  const options = (raw?.options ?? []).map(normalizeOption).sort((a, b) => a.order - b.order);
  const storedTotal = Number(raw?.totalVotes);
  const totalVotes = Number.isFinite(storedTotal)
    ? storedTotal
    : options.reduce((sum, option) => sum + option.votes, 0);
  const storedRespondents = Number(raw?.respondents);
  const votedOptionIds = raw?.userVotedOptionIds
    ?? (raw?.userVotedOptionId ? [raw.userVotedOptionId] : []);

  return {
    id: String(raw?.id ?? (index === 0 ? LEGACY_QUESTION_ID : `q${index + 1}`)),
    text: String(raw?.text ?? ""),
    order: Number.isFinite(Number(raw?.order)) ? Number(raw?.order) : index,
    options,
    allowMultiple: raw?.allowMultiple === true,
    totalVotes,
    // One answer each is what a question written before this meant.
    respondents: Number.isFinite(storedRespondents) ? storedRespondents : totalVotes,
    userVotedOptionId: votedOptionIds[0],
    userVotedOptionIds: votedOptionIds,
  };
}

/**
 * Reads a poll from the API into the shape the components expect, whichever
 * shape it arrives in: a copy stored on a post or space that nobody has
 * touched since polls gained several questions still holds just one.
 */
export function normalizePoll(raw: RawPoll | Poll | null | undefined): Poll {
  const source = (raw ?? {}) as RawPoll;

  const rawQuestions: RawQuestion[] =
    source.questions && source.questions.length > 0
      ? source.questions
      : [
          {
            id: LEGACY_QUESTION_ID,
            text: source.question ?? "",
            options: source.options ?? [],
            order: 0,
            userVotedOptionId: source.userVotedOptionId,
          },
        ];

  const questions = rawQuestions
    .map(normalizeQuestion)
    .sort((a, b) => a.order - b.order);

  const totalVotes = questions.reduce((sum, question) => sum + question.totalVotes, 0);

  return {
    id: source.id,
    ownerType: source.ownerType,
    ownerId: source.ownerId,
    questions,
    voterVisibility: source.voterVisibility ?? DEFAULT_VOTER_VISIBILITY,
    totalVotes,
    respondents: typeof source.respondents === "number" ? source.respondents : totalVotes,
    endsAt: source.endsAt as Timestamp,
    userVoted:
      source.userVoted ?? questions.some((question) => Boolean(question.userVotedOptionId)),
    userVotedOptionId: source.userVotedOptionId ?? questions[0]?.userVotedOptionId,
  };
}

export function pollHasEnded(poll: Poll): boolean {
  return toDate(poll.endsAt).getTime() <= Date.now();
}

export function answeredCount(poll: Poll): number {
  return poll.questions.filter((question) => isAnswered(question)).length;
}

export function isAnswered(question: PollQuestion): boolean {
  return (question.userVotedOptionIds ?? []).length > 0;
}

/**
 * Out of the people who answered, not the votes: on a question that takes
 * several answers the percentages would otherwise add up past 100.
 */
export function percentageOf(option: PollOption, question: PollQuestion): number {
  const base = question.respondents || question.totalVotes;
  if (!base) return 0;
  return Math.round((option.votes / base) * 100);
}

/** A poll ready to be edited: ids are kept so votes survive the edit. */
export function toCreatePollData(poll: Poll): CreatePollData {
  return {
    voterVisibility: poll.voterVisibility ?? DEFAULT_VOTER_VISIBILITY,
    questions: poll.questions.map((question, questionIndex) => ({
      id: question.id,
      text: question.text,
      order: questionIndex,
      allowMultiple: question.allowMultiple,
      options: question.options.map((option, optionIndex) => ({
        id: option.id,
        text: option.text,
        order: optionIndex,
      })),
    })),
    endsAt: toDate(poll.endsAt),
  };
}
